import { useEffect, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "../../store";
import { buildMenuTree, getAllDescendants } from "../../utility/menuUtils";
import { getTopLevelMenus, isTmsModule, pathMatchesMenu } from "../../utility/layoutTheme";
import { resolveMenuNavUrl } from "../../utility/tmsRouteUtils";
import { applyTmsMenuIcons, resolveTmsMenuIcon } from "../../utility/tmsSidebarMenu";

function withResolvedIcons(nodes: any[]): any[] {
    return (nodes || []).map((node) => ({
        ...node,
        icon: resolveTmsMenuIcon(node),
        children: withResolvedIcons(node.children || []),
    }));
}

const Sidebar = ({
    handleNavFunc,
    parentCode,
}: any) => {
    const userSlice = useAppSelector((state: any) => state.userSlice);
    const location = useLocation();
    const [menuTree, setMenuTree] = useState<any[]>([]);

    const topMenus = getTopLevelMenus(userSlice?.user?.modulePermission);
    const activeTopMenu = topMenus.find((m: any) => m.code === parentCode);
    const isTmsModuleActive = isTmsModule(activeTopMenu);
    const usePortalLayout = topMenus.length > 0;

    useEffect(() => {
        if (parentCode) {
            let modulePermission = userSlice?.user?.modulePermission?.filter((f: any) => f?.isIncludeMenu === true);
            const allChildMenus = getAllDescendants(modulePermission, parentCode);
            const tree = buildMenuTree(allChildMenus);
            const menuWithIcons = isTmsModuleActive ? applyTmsMenuIcons(tree) : withResolvedIcons(tree);
            setMenuTree(menuWithIcons);
        }
    }, [userSlice, parentCode, isTmsModuleActive]);

    return (
        <>
            <nav className={`sidebar sidebar-offcanvas ${usePortalLayout ? "tms-sidebar" : ""}`} id="sidebar">
                {usePortalLayout ? (
                    <TmsMenuList
                        items={menuTree}
                        handleNavFunc={handleNavFunc}
                        pathname={location.pathname}
                        search={location.search}
                    />
                ) : (
                    <CmsMenuList items={menuTree} handleNavFunc={handleNavFunc} level={0} />
                )}
                {usePortalLayout && (
                    <>
                        <TmsHelpCard isTms={isTmsModuleActive} />
                        <div className="tms-sidebar-footer">
                            © 2023 Genius World. All rights reserved.
                        </div>
                    </>
                )}
            </nav>
        </>
    )
};

const CmsMenuList = ({ items, handleNavFunc, level }: any) => {
    const [collapse, setcollapse] = useState(null);
    const toggleFunc = (val: any) => {
        const prevValue = collapse;
        if (prevValue === val)
            setcollapse(null);
        else
            setcollapse(val);
    };
    return (
        <ul className={`nav ${level == 1 ? "flex-column sub-menu" : ""}`}>
            {items?.map((item: any) => (
                <li key={`sidebarmenu_${item?.id}`} className={`nav-item ${collapse == item?.code ? "" : ""}`}>
                    <a className="nav-link" data-bs-toggle={collapse == item?.code ? "collapse" : ""} aria-expanded={collapse == item?.code ? "true" : "false"} onClick={() => {
                        if (item?.url)
                            handleNavFunc(item?.url);
                        toggleFunc(item?.code);
                    }}  >
                        {item?.icon && <i className={`${item.icon} menu-icon`}></i>}
                        <span className="menu-title">{item.moduleName}</span>
                        {!item?.url && <i className="menu-arrow"></i>}
                    </a>
                    {item.children && item.children.length > 0 && (
                        <SubMenuCollapse show={collapse == item?.code}>
                            <CmsMenuList items={item.children} handleNavFunc={handleNavFunc} level={1} />
                        </SubMenuCollapse>
                    )}
                </li>
            ))}
        </ul>
    );
};

function usesFlyoutSubmenu(item: any): boolean {
    return item?.submenuStyle !== "inline";
}

function tmsMenuItemIsActive(pathname: string, item: any, search: string): boolean {
    if (pathMatchesMenu(pathname, resolveMenuNavUrl(item), search)) return true;
    return item?.children?.some((child: any) => tmsMenuItemIsActive(pathname, child, search)) ?? false;
}

const TmsMenuList = ({ items, handleNavFunc, pathname, search }: any) => {
    const [openFlyoutCode, setOpenFlyoutCode] = useState<string | null>(null);
    const [inlineExpandedCode, setInlineExpandedCode] = useState<string | null>(null);
    const sidebarRef = useRef<HTMLUListElement>(null);

    useEffect(() => {
        const closeOnOutside = (e: MouseEvent) => {
            if (!sidebarRef.current?.contains(e.target as Node)) {
                setOpenFlyoutCode(null);
            }
        };
        document.addEventListener("mousedown", closeOnOutside);
        return () => document.removeEventListener("mousedown", closeOnOutside);
    }, []);

    const handleItemClick = (e: ReactMouseEvent, item: any, hasChildren: boolean) => {
        e.preventDefault();
        if (hasChildren) {
            if (usesFlyoutSubmenu(item)) {
                setOpenFlyoutCode((prev) => (prev === item.code ? null : item.code));
            } else {
                setInlineExpandedCode((prev) => (prev === item.code ? null : item.code));
            }
            return;
        }
        setOpenFlyoutCode(null);
        const navUrl = resolveMenuNavUrl(item);
        if (navUrl) handleNavFunc(navUrl);
    };

    return (
        <ul className="nav tms-nav-root" ref={sidebarRef}>
            {items?.map((item: any) => {
                const hasChildren = item.children?.length > 0;
                const isFlyoutMenu = hasChildren && usesFlyoutSubmenu(item);
                const isFlyoutOpen = isFlyoutMenu && openFlyoutCode === item.code;
                const isInlineOpen = hasChildren && !isFlyoutMenu && inlineExpandedCode === item.code;
                const isTopActive = tmsMenuItemIsActive(pathname, item, search);
                const iconClass = resolveTmsMenuIcon(item);

                return (
                    <li
                        key={`sidebarmenu_${item?.id ?? item?.code}`}
                        className={`nav-item ${isTopActive ? "active" : ""} ${hasChildren ? "tms-has-children" : ""} ${isFlyoutMenu ? "tms-has-flyout" : ""} ${isFlyoutOpen ? "tms-flyout-open" : ""} ${isInlineOpen ? "tms-inline-open" : ""}`}
                    >
                        <div className="tms-nav-item-wrap">
                            <a
                                className="nav-link"
                                href={resolveMenuNavUrl(item) || "#"}
                                aria-expanded={hasChildren ? (isFlyoutOpen || isInlineOpen) : undefined}
                                onClick={(e) => handleItemClick(e, item, hasChildren)}
                            >
                                <i className={`${iconClass} menu-icon`} aria-hidden="true" />
                                <span className="menu-title">{item.moduleName}</span>
                                {hasChildren && (
                                    <i
                                        className={`${isFlyoutOpen || isInlineOpen ? "ti-angle-down" : "ti-angle-right"} tms-flyout-chevron`}
                                        aria-hidden="true"
                                    />
                                )}
                            </a>
                            {isFlyoutOpen && (
                                <TmsFloatingSubmenu
                                    items={item.children}
                                    title={item.moduleName}
                                    pathname={pathname}
                                    search={search}
                                    onClose={() => setOpenFlyoutCode(null)}
                                    handleNavFunc={(url: string) => {
                                        setOpenFlyoutCode(null);
                                        handleNavFunc(url);
                                    }}
                                />
                            )}
                            {isInlineOpen && (
                                <TmsInlineSubmenu
                                    items={item.children}
                                    pathname={pathname}
                                    search={search}
                                    handleNavFunc={handleNavFunc}
                                />
                            )}
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};

function TmsInlineSubmenu({
    items,
    pathname,
    search,
    handleNavFunc,
}: {
    items: any[];
    pathname: string;
    search: string;
    handleNavFunc: (url: string) => void;
}) {
    return (
        <ul className="tms-nav-sub">
            {items.map((child: any) => {
                const childUrl = resolveMenuNavUrl(child);
                const isActive =
                    pathMatchesMenu(pathname, childUrl, search) || tmsMenuItemIsActive(pathname, child, search);
                const iconClass = resolveTmsMenuIcon(child);
                return (
                    <li
                        key={`tms_inline_${child?.id ?? child?.code}`}
                        className={`tms-nav-sub__item ${isActive ? "active" : ""}`}
                    >
                        <a
                            className="tms-nav-sub__link"
                            href={childUrl || "#"}
                            onClick={(e) => {
                                if (childUrl) {
                                    e.preventDefault();
                                    handleNavFunc(childUrl);
                                }
                            }}
                        >
                            <i className={`${iconClass} menu-icon`} aria-hidden="true" />
                            <span className="menu-title">{child.moduleName}</span>
                        </a>
                    </li>
                );
            })}
        </ul>
    );
}

function TmsFloatingSubmenu({
    items,
    title,
    pathname,
    search,
    handleNavFunc,
    onClose,
    nested = false,
}: {
    items: any[];
    title?: string;
    pathname: string;
    search: string;
    handleNavFunc: (url: string) => void;
    onClose?: () => void;
    nested?: boolean;
}) {
    const [openNestedCode, setOpenNestedCode] = useState<string | null>(null);

    return (
        <div
            className={`tms-flyout ${nested ? "tms-flyout--nested" : ""}`}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
        >
            {title && !nested && (
                <div className="tms-flyout__header">
                    <span className="tms-flyout__title">{title}</span>
                    {onClose && (
                        <button
                            type="button"
                            className="tms-flyout__close"
                            aria-label="Close menu"
                            onClick={onClose}
                        >
                            <i className="ti-close" aria-hidden="true" />
                        </button>
                    )}
                </div>
            )}
            <ul className="tms-flyout__list">
                {items.map((child: any) => {
                    const hasChildren = child.children?.length > 0;
                    const childUrl = resolveMenuNavUrl(child);
                    const isActive =
                        pathMatchesMenu(pathname, childUrl, search) || tmsMenuItemIsActive(pathname, child, search);
                    const iconClass = resolveTmsMenuIcon(child);
                    const isNestedOpen = openNestedCode === child.code;

                    return (
                        <li
                            key={`tms_flyout_${child?.id ?? child?.code}`}
                            className={`tms-flyout__item ${isActive ? "tms-flyout__item--active" : ""} ${hasChildren ? "tms-flyout__item--has-children" : ""} ${isNestedOpen ? "tms-flyout__item--open" : ""}`}
                        >
                            <div className="tms-flyout__item-wrap">
                                <a
                                    className="tms-flyout__link"
                                    href={childUrl || "#"}
                                    onClick={(e) => {
                                        if (hasChildren) {
                                            e.preventDefault();
                                            setOpenNestedCode((prev) => (prev === child.code ? null : child.code));
                                            return;
                                        }
                                        if (childUrl) {
                                            e.preventDefault();
                                            handleNavFunc(childUrl);
                                        }
                                    }}
                                >
                                    <i className={`${iconClass} tms-flyout__icon`} aria-hidden="true" />
                                    <span className="tms-flyout__label">{child.moduleName}</span>
                                    {hasChildren && <i className="ti-angle-right tms-flyout__chevron" aria-hidden="true" />}
                                </a>
                                {hasChildren && isNestedOpen && (
                                    <TmsFloatingSubmenu
                                        items={child.children}
                                        pathname={pathname}
                                        search={search}
                                        handleNavFunc={handleNavFunc}
                                        nested
                                    />
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function SubMenuCollapse({ show, children }: { show: boolean; children: React.ReactNode }) {
    return (
        <div className={show ? "collapse show" : "collapse"}>
            {children}
        </div>
    );
}

function TmsHelpCard({ isTms = true }: { isTms?: boolean }) {
    return (
        <div className="tms-sidebar-help">
            <h6>Need help?</h6>
            <p>
                {isTms
                    ? "Contact our support team for assistance with TMS features."
                    : "Contact our support team for assistance with CMS features."}
            </p>
            <button type="button" className="btn-contact-support">Contact Support</button>
        </div>
    );
}

export default Sidebar
